import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import intervalToDuration from 'date-fns/intervalToDuration';
import Button from 'components/common/Button';
import Map from 'components/common/Map';
import Direction from 'components/common/Map/Direction';
import { Form, Formik } from 'formik';
import { FreightsService } from 'service/FreightsService';
import { showErrorMessage, showSuccessMessage } from 'helpers/notifications';

import './companyRoute.scss';
import { BUTTON_SIZE } from '../../Button';
import FormikTextField from '@/components/InputsField/formikInputs/FormikTextField';
import { useSelector } from 'react-redux';

export default function CompanyRoute({ loadingFrom, loadingTo, data, searchCoordinates }) {
  const { t } = useTranslation();
  const profileData = useSelector((state) => state.profile.profileData);

  const [distance, setDistance] = useState(data?.distance || '');
  const [time, setTime] = useState('');
  const [isEditing, setIsEditing] = useState(!data?.gps_tracking_link);

  const formattedTime = useMemo(() => {
    if (data?.distance_time) {
      const hours = Math.floor(parseFloat(data.distance_time));
      const minutes = Math.round((parseFloat(data.distance_time) - hours) * 60);
      return `${hours ? `${hours}${t('units.hour')} ` : ''}${minutes}${t('units.minute')}`;
    }
    return null;
  }, [data?.distance_time, t]);

  const handleSetDirectionInfo = useCallback(
    (directionData) => {
      if (!data?.distance && directionData.totalDistance) {
        setDistance((directionData.totalDistance / 1000).toFixed(2));
      }
      if (!data?.distance_time && directionData.totalTime) {
        const duration = intervalToDuration({
          start: 0,
          end: directionData.totalTime * 1000,
        });
        setTime(
          `${duration.days ? `${duration.days}${t('units.day')} ` : ''}${
            duration.hours ? `${duration.hours}${t('units.hour')} ` : ''
          }${duration.minutes}${t('units.minute')}`,
        );
      }
    },
    [data?.distance, data?.distance_time, t],
  );

  const wayPoints = useMemo(
    () => [
      ...loadingFrom.map((item) => ({ name: 'uploading', ...item })),
      ...loadingTo.map((item) => ({ name: 'unloading', ...item })),
    ],
    [loadingFrom, loadingTo],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        gps_tracking_link: Yup.string()
          .matches(/^https?:\/\/.+/, { message: t('errors.invalidUrl'), excludeEmptyString: true })
          .max(2048)
          .nullable(),
      }),
    [t],
  );

  const handleSubmit = async (values) => {
    try {
      await FreightsService.updateGpsTrackingLink(data.id, values.gps_tracking_link);
      data.gps_tracking_link = values.gps_tracking_link;
      setIsEditing(false);
      showSuccessMessage(t('success'));
    } catch (e) {
      console.error(e);
      showErrorMessage(t('error'));
    }
  };

  const handleDeleteLink = async (setFieldValue) => {
    try {
      await FreightsService.updateGpsTrackingLink(data.id, null);
      data.gps_tracking_link = null;
      setFieldValue('gps_tracking_link', '');
      setIsEditing(true);
      showSuccessMessage(t('success'));
    } catch (e) {
      console.error(e);
      showErrorMessage(t('error'));
    }
  };

  const isDealConfirmed = data?.owner_status && data?.client_status;
  const isOwner = data && profileData && profileData.id === data.user_id;
  const isClient = data?.isActiveProposal && !isOwner;

  return (
    <>
      {isClient && (
        <Formik
          initialValues={{ gps_tracking_link: data?.gps_tracking_link ?? null }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, submitForm, values, setFieldValue }) => (
            <Form>
              <section className="cargo-tracking">
                <h3 className="cargo-tracking__title">
                  {t('cargoTracking.title')}
                </h3>
                <div className="cargo-tracking__input-row">
                    <FormikTextField
                      name="gps_tracking_link"
                      label={t('cargoTracking.linkLabel')}
                      wrapperClassName="cargo-tracking__input"
                      disabled={!isDealConfirmed || !isEditing}
                      errorMode="top-right"
                    />
                    {isEditing ? (
                      <Button
                        size={BUTTON_SIZE.small}
                        className="cargo-tracking__button"
                        onClick={submitForm}
                        type="button"
                        disabled={
                          !isDealConfirmed ||
                          !values.gps_tracking_link ||
                          values.gps_tracking_link === data?.gps_tracking_link
                        }
                      >
                        {data?.gps_tracking_link ? t('save') : t('cargoTracking.addLinkButton')}
                      </Button>
                    ) : (
                      <Button
                        size={BUTTON_SIZE.small}
                        className="cargo-tracking__button"
                        onClick={() => setIsEditing(true)}
                        type="button"
                        disabled={!isDealConfirmed}
                      >
                        {t('cargoTracking.editLinkButton')}
                      </Button>
                    )}
                    {data?.gps_tracking_link && (
                      <Button
                        size={BUTTON_SIZE.small}
                        className="cargo-tracking__button cargo-tracking__button--delete"
                        onClick={() => handleDeleteLink(setFieldValue)}
                        type="button"
                        disabled={!isDealConfirmed}
                      >
                        {t('cargoTracking.deleteLinkButton')}
                      </Button>
                    )}
                  </div>
                {!isDealConfirmed && !data?.gps_tracking_link && (
                  <div className="cargo-tracking__notification">
                    <img
                      className="cargo-tracking__notification-icon"
                      src={new URL('../../../../img/important-blue.svg', import.meta.url).href}
                      alt="important"
                    />
                    <p className="cargo-tracking__notification-text">{t('cargoTracking.notificationText')}</p>
                  </div>
                )}
              </section>
            </Form>
          )}
        </Formik>
      )}

      <ul className="company-popup__route">
        <li className="company-popup__route-item">
          <p className="company-popup__route-data">{t('exchangeModal.mainDistance')}</p>
          <span>
            {distance ? `${distance} ${t('units.km')}` : '-'}
          </span>
        </li>
        <li className="company-popup__route-item">
          <p className="company-popup__route-data">{t('exchangeModal.estimatedTime')}</p>
          <span>{formattedTime || time || '-'}</span>
        </li>
      </ul>
      <div className="company-popup__container">
        <div className="company-popup__map">
          <Map currentPosition={loadingFrom[0]}>
            <Direction
              setDirectionInfo={(data?.distance || data?.distance_time) ? () => {} : handleSetDirectionInfo}
              points={wayPoints}
              searchCoordinates={searchCoordinates}
            />
          </Map>
        </div>
      </div>
    </>
  );
}
